import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';

// Helper function to convert File to ArrayBuffer
const fileToArrayBuffer = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

// Initialize the S3 client with credentials from environment variables
const s3Client = new S3Client({
  region: process.env.REACT_APP_AWS_REGION || 'ap-south-1',
  credentials: {
    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID || '',
    secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY || '',
  },
  // Enable this for debugging
  logger: console,
  // Force path style for S3 URLs (required for some configurations)
  forcePathStyle: true,
  // Configure signature version
  signatureVersion: 'v4',
  // Disable checking for AWS specific headers
  s3DisableBodySigning: false
});

/**
 * Uploads a file to S3
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Callback function to track upload progress
 * @param {string} folder - The folder in S3 where the file should be stored
 * @returns {Promise<string>} - The URL of the uploaded file
 */
export const uploadFileToS3 = async (file, onProgress, folder = 'company-documents') => {
  if (!file) {
    throw new Error('No file provided');
  }

  // Generate a unique file key with folder and timestamp
  const safeFileName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '');
  const fileKey = `${folder}/${Date.now()}-${safeFileName}`;
  
  // Set up the upload parameters
  const params = {
    Bucket: process.env.REACT_APP_S3_BUCKET_NAME || 'var80-knowledge-base',
    Key: fileKey,
    Body: file,
    ContentType: file.type || 'application/octet-stream',
    // No ACL needed - using bucket policies for access control
    // Enable server-side encryption
    ServerSideEncryption: 'AES256',
    // Add metadata
    Metadata: {
      'uploaded-by': 'var80-web-app',
      'original-filename': file.name
    }
  };

  console.log('Uploading file with params:', {
    bucket: params.Bucket,
    key: params.Key,
    type: params.ContentType,
    size: file.size
  });

  try {
    // Convert file to ArrayBuffer
    const arrayBuffer = await fileToArrayBuffer(file);
    params.Body = arrayBuffer;
    
    // Upload the file directly to S3
    const command = new PutObjectCommand(params);
    const response = await s3Client.send(command);
    
    console.log('S3 Upload Response:', response);
    
    // Construct and return the public URL
    const region = process.env.REACT_APP_AWS_REGION || 'ap-south-1';
    const url = `https://${params.Bucket}.s3.${region}.amazonaws.com/${fileKey}`;
    
    console.log('File uploaded successfully. URL:', url);
    return url;
  } catch (error) {
    console.error('S3 Upload Error:', {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack,
      params: {
        Bucket: params.Bucket,
        Key: params.Key,
        ContentType: params.ContentType
      }
    });
    throw new Error(`Failed to upload file to S3: ${error.message}`);
  }
};

export default {
  uploadFileToS3,
};
